<template>

	<div class="no-gutters text-center pb-3">
		<span property="itemListElement" typeof="ListItem">
			<a property="item" typeof="WebPage" title="Go to EveryLink." href="/" class="home">
				<span property="name">EveryLink</span>
			</a>
			<meta property="position" content="1">
		</span>
		<span class="d"> &gt; </span>
		<span property="itemListElement" typeof="ListItem">
			<span property="name">People</span>
			<meta property="position" content="3">
		</span>
	</div>

	<div>
		<div v-if="isLoading" class="d-flex justify-content-center gap-3 mb-2">
			<Skeletor v-for="index in 3" :key="index" width="140" height="46" pill />
		</div>
		<div v-else>
			<ChatPeopleBar v-if="getFriends?.length" :peopleList="getFriends" />
		</div>
	</div>

	<div class="d-flex justify-content-center">
		<Skeletor class="my-3" v-if="isLoading" circle size="120" />
		<div v-else class="avatar my-3" :style="{ backgroundImage: `url(${getAvatar})` }"></div>
	</div>

	<div class="row no-gutters">
		<div class="col-12 text-center">
			<div>
				<Skeletor class="mx-auto mb-2" v-if="isLoading" width="300" height="35" />
				<h2 v-else class="d-inline-block align-middle">
					{{ getFullName }}
				</h2>
			</div>
			<span v-if="getFriendRequests" class="badge bg-primary d-inline-block">{{ getFriendRequests }}</span>
		</div>
	</div>

	<div class="row no-gutters">
		<div class="col-12 text-center">
			<p>
				<a href="/profile-edit" class="mb-3 d-block link-secondary link-offset-2 link-underline-opacity-25 link-underline-opacity-100-hover">Edit
					Profile</a>
			</p>
		</div>
	</div>

	<div class="row no-gutters">
		<div class="col">
			<form @submit.prevent="sendPost" class="my-5">
				<div class="row no-gutters justify-content-center">
					<div class="col-12 col-sm-10 col-md-10 col-lg-8 col-xl-6">
						<div class="pb-3 text-center">
							<div class="input-group">
								<input ref="inputFile" @change="onFileChange" class="form-control" type="file" id="formFile" name="avatar" accept="image/*">
								<button @click="uploadFile" class="btn btn-primary" type="button" name="submit">Add
									Photo
								</button>
							</div>
						</div>
						<textarea
							v-model="linkInput"
							@keydown.enter.exact.prevent="sendPost"
							type="text"
							placeholder="What's up?"
							class="form-control search-field bg-light mb-2 tablinx-people__textarea"
						/>
						<div class="pt-3">
							<button type="submit" class="btn btn-lg btn-primary">Post</button>
							<small class="ms-3">
								<small>Or press ENTER to post...</small>
							</small>
						</div>
					</div>
				</div>
			</form>
		</div>
	</div>

	<div v-if="isLoading" class="home">
		<div class="row">
			<div v-for="index in 3" :key="index" class="col-12 col-md-6 col-lg-4 mb-4">
				<Skeletor width="100%" height="430" />
			</div>
		</div>
	</div>
	<div v-else class="home">
		<div class="row" v-if="getPosts.length">
			<PostCard v-for="post in getPosts" :key="post._id" :post="post" :currentUserId="currentUser.wpId" />
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import ChatPeopleBar from '@/components/ChatPeopleBar.vue';
import PostCard from '@/components/PostCard/PostCard.vue';
import axios from 'axios';
import io from 'socket.io-client';

type Message = {
	_id: number;
	author: string;
	message: string;
	isRead: boolean
	createdAt: Date;
	status: string;
	updated_at: string;
};

interface IPost {
	_id: number
	_v: number
	author: object
	authorContent: string
	metaInput: object
	title: string
	description: string
	updated_at: string
	created_at: string
}

/* eslint-disable */
interface IUser {
	_id?: number
	wpId: number
	firstName: string
	lastName: string
	email: string
	friendRequests?: object[]
	friends?: IFriend[],
	avatar?: string
	posts?: IPost[]
	unreadMessage?: number
	chatRooms: string[]
}

interface IFriend {
	online?: boolean,
	blockFriend: boolean,
	_id: IUser
}

export default defineComponent({
	name: 'HomeView',
	components: {
		ChatPeopleBar,
		PostCard
	},
	data(){
		return {
			selectedFile: null as File | null,
			wpUser: null as IUser | null,
			currentUser: null as IUser | null,
			linkInput: '',
			allPosts: [] as IPost[],
			isLoading: false,
			selectedFileId: '',
			socket: io(''),
		};
	},
	computed: {
		getFullName(){
			return this.currentUser?.firstName + ' ' + this.currentUser?.lastName
		},
		getAvatar(){
			return this.currentUser?.avatar
		},
		getFriendRequests(){
			return this.currentUser?.friendRequests?.length || null
		},
		getFriends(){
			const friends: IFriend[] = this.currentUser?.friends || [];
			return friends.filter((friend: IFriend) => !friend.blockFriend).sort((a: IFriend, b: IFriend) => {
				const unreadMessagesA = a._id.unreadMessage || 0;
				const unreadMessagesB = b._id.unreadMessage || 0;

				return unreadMessagesB - unreadMessagesA;
			})
		},
		getPosts(){
			const posts: IPost[] = this.getFriends ? (this.allPosts || []) : (this.currentUser?.posts || []);
			return posts.sort((a: IPost, b: IPost) => {
				const dateA: Date = new Date(a.created_at);
				const dateB: Date = new Date(b.created_at);
				return dateB.getTime() - dateA.getTime();
			});
		}
	},
	created(){
		this.socket = io(`${process.env.VUE_APP_BASE_API_URI_SOCKET}`)
		this.socket.on('connect', () => {
			console.log('Connected to the server');
		});
	},
	async mounted(){
		this.isLoading = true
		await this.getUserFromWp()
		await this.getUser()
		if (!this.currentUser){
			await this.createUser()
			await this.getUser()
		}
		await this.getUnreadMessages()
		this.socket.emit('join', { user: this.currentUser?._id })
		this.socket.on('updateOnlineUsers', (onlineUsers) => {
			this.currentUser?.friends?.forEach(person => {
				const matchingUser = onlineUsers.find((user: {socketId: string, user: number}) => user.user === person._id._id);
				person.online = !!matchingUser;
			});
		});
		this.socket.on('allMessages', (data) => {
			this.currentUser?.friends?.forEach(person => {
				if (Array.isArray(person._id.chatRooms) && person._id.chatRooms.includes(data.chatId)){
					person._id.unreadMessage = data.messages;
				}
				return person;
			});
		});
		this.isLoading = false
	},
	methods: {
		onFileChange(e: Event){
			const target = e.target as HTMLInputElement
			const file: File = (target.files as FileList)[0]
			this.selectedFile = file;
		},
		async uploadFile(){
			try {
				const formData = new FormData();
				formData.set('file', this.selectedFile as File);

				const res = await axios.post(`${process.env.VUE_APP_BASE_API_URI}/posts/${this.currentUser?._id}/upload`, formData)
				this.selectedFileId = res.data.fileId
			} catch (err){
				console.log(err)
			}
		},
		async getUserFromWp(){
			try {
				const res = await axios.get('/api/get_current_user.php')
				this.wpUser = res.data
			} catch (err){
				console.log(err)
			}
		},
		async getUser(){
			try {
				const res = await axios.get(`${process.env.VUE_APP_BASE_API_URI}/users/${this.wpUser?.wpId}`)
				this.currentUser = res.data
				const myPosts = this.currentUser?.posts as []
				this.allPosts = myPosts
				if (res.data.friends.length){
					const friendsPosts = await this.getFriendsPosts()
					this.allPosts = [...myPosts, ...friendsPosts]
				}
			} catch (err){
				console.log(err)
			}
		},
		async createUser(){
			try {
				await axios.post(`${process.env.VUE_APP_BASE_API_URI}/users/create`, this.wpUser as IUser)
			} catch (err){
				console.log(err)
			}
		},
		async sendPost(){
			try {
				await axios.post(`${process.env.VUE_APP_BASE_API_URI}/posts/create`, {
					url: this.linkInput,
					author: this.currentUser?._id,
					authorImage: this.selectedFileId
				})
				if (this.$refs.inputFile){
					(this.$refs.inputFile as HTMLInputElement).value = '';
				}
				this.linkInput = ''
				this.selectedFileId = ''

				await this.getUser()
			} catch (err){
				console.log(err)
			}
		},
		async getFriendsPosts(){
			try {
				const res = await axios.get(`${process.env.VUE_APP_BASE_API_URI}/posts/${this.currentUser?._id}/friends`)
				return res.data
			} catch (err){
				console.log(err)
			}
		},
		async getUnreadMessages(){
			try {
				const response = await axios.get(`${process.env.VUE_APP_BASE_API_URI}/chat/${this.currentUser?._id}`);
				const messages = response.data;

				const friendsMap = new Map(this.currentUser?.friends?.map((friend) => [String(friend._id._id), friend]));

				messages.forEach((message: Message) => {
					const friend = friendsMap.get(String(message.author));
					if (friend) {
						friend._id.unreadMessage = (friend._id.unreadMessage || 0) + 1;
					}
				});
			} catch (err) {
				console.error(err);
			}
		}
	},
	unmounted(){
		if (this.socket) {
			this.socket.disconnect();
			console.log('Socket.IO connection closed');
		}
	}
})
</script>
